/** Preflight tab of the tour-operator edit page.
 *
 * Per-TO onboarding checklist with the Phase 0 action rails, rendered as one
 * dense grouped table of expandable rows. Each applicable row is a single line
 * — status control, meta, and the shared action bar (Preview, the item's one
 * primary verb, and an overflow) — and expands (chevron) to reveal its rendered
 * step help text, the step editor, and any error.
 *
 * A tri-state control sets the bulk expansion (persisted in localStorage):
 *   - **Collapsed** — every row is a one-liner;
 *   - **Default**   — completed rows collapsed, everything still needing
 *                     attention (pending / started / error) expanded;
 *   - **Expanded**  — every row expanded.
 * Per-row chevrons override the bulk state for that item.
 *
 * Mutations are gated on `console_preflight_manage` (otherwise status is a
 * read-only chip and only "View log" shows). Setting a status goes through the
 * shared `PreflightStatusControl`, so "Completed" runs the action's own
 * validation rather than writing the column behind its back. While any item is
 * `started` the list polls so async runs surface as they complete. The backend
 * is always the authoritative check.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { items } from '@/api/preflight';
import type { PreflightActionRun, PreflightItem, PreflightStatus } from '@/api/preflight';
import { AssignedGroupLink } from '@/features/preflight/AssignedGroupLink';
import { BlockedChip } from '@/features/preflight/PreflightBlockers';
import { PreflightItemActionBar } from '@/features/preflight/PreflightItemActionBar';
import { PreflightStatusControl } from '@/features/preflight/PreflightStatusControl';
import { PREFLIGHT_STATUSES, STATUS_LABELS, statusChipColor } from '@/features/preflight/status';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { usePermissions } from '@/hooks/usePermissions';

import { PreflightItemDetailContent } from './PreflightItemDetailContent';
import { PreflightItemRunLogDrawer, PreflightPreviewDialog } from './PreflightRunLog';
import { usePreflightItemActions } from './usePreflightItemActions';

interface Props {
  to: TourOperatorDetail;
  onSaved?: () => void;
  /** URL segments after ".../preflight" — unused (no sub-routing yet). */
  path?: string[];
  /** Replace the URL segments after ".../preflight" — unused. */
  onNavigate?: (sub: string[]) => void;
}

/** Number of columns in the table (chevron + status + item + owner + step + actions).
 *  The raw `task_type` enum used to hold a 96px column printing `manual` /
 *  `async` / `multistep` — an implementation detail in the operator's scan path,
 *  while the item title truncated beside it. */
const COLS = 6;

type ExpandMode = 'collapsed' | 'default' | 'expanded';
const EXPAND_KEY = 'preflight.expandMode';

function readExpandMode(): ExpandMode {
  try {
    const v = localStorage.getItem(EXPAND_KEY);
    return v === 'collapsed' || v === 'expanded' || v === 'default' ? v : 'default';
  } catch {
    return 'default';
  }
}

/** Bulk expansion for an item under a given mode (before any per-row override). */
function defaultExpanded(item: PreflightItem, mode: ExpandMode): boolean {
  if (mode === 'collapsed') return false;
  if (mode === 'expanded') return true;
  return item.status !== 'completed'; // 'default': show what still needs attention
}

const STATUSES = PREFLIGHT_STATUSES;

/** Bucket the pre-ordered flat list into groups, preserving arrival order. */
function groupItems(list: PreflightItem[]): { code: string; name: string; items: PreflightItem[] }[] {
  const order: string[] = [];
  const byCode = new Map<string, { code: string; name: string; items: PreflightItem[] }>();
  for (const item of list) {
    let bucket = byCode.get(item.group_code);
    if (!bucket) {
      bucket = { code: item.group_code, name: item.group_name || item.group_code, items: [] };
      byCode.set(item.group_code, bucket);
      order.push(item.group_code);
    }
    bucket.items.push(item);
  }
  return order.map((code) => byCode.get(code)!);
}

/** Applicable items first, then inapplicable — each keeping its arrival order. */
function applicableFirst(list: PreflightItem[]): PreflightItem[] {
  return [...list.filter((i) => i.applicable), ...list.filter((i) => !i.applicable)];
}

export function PreflightTab({ to }: Props) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_preflight_manage');

  const [expandMode, setExpandMode] = useState<ExpandMode>(readExpandMode);
  const [overrides, setOverrides] = useState<Record<number, boolean>>({});
  const [search, setSearch] = useState('');
  const [logItem, setLogItem] = useState<PreflightItem | null>(null);

  // Switching the bulk mode is authoritative — it clears per-row overrides.
  const changeMode = (next: ExpandMode | null) => {
    if (!next) return;
    setOverrides({});
    setExpandMode(next);
    try {
      localStorage.setItem(EXPAND_KEY, next);
    } catch {
      /* ignore storage failures */
    }
  };
  const isExpanded = (item: PreflightItem) => overrides[item.id] ?? defaultExpanded(item, expandMode);
  const toggleRow = (item: PreflightItem) =>
    setOverrides((o) => ({ ...o, [item.id]: !isExpanded(item) }));

  // "Waiting on X" is only useful if X is reachable. Expand the blocking row and
  // bring it into view; a blocker filtered out by the search is surfaced by
  // clearing the filter first, so the click never silently does nothing.
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const jumpTo = (id: number) => {
    setSearch('');
    setOverrides((o) => ({ ...o, [id]: true }));
    requestAnimationFrame(() => {
      rowRefs.current[id]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['preflight', 'items', to.id],
    queryFn: () => items.list(to.id),
    // Poll while any item is mid-run so async executions surface as they finish.
    refetchInterval: (q) => ((q.state.data ?? []).some((i) => i.status === 'started') ? 4000 : false),
  });

  const actions = usePreflightItemActions(to.id);

  const handlers = (item: PreflightItem) => ({
    onStatusChange: (status: PreflightStatus) => actions.onStatusChange(item.id, status),
    onStepChange: (step: number, status?: PreflightStatus) => actions.onStepChange(item.id, step, status),
    onPreview: () => actions.onPreview(item.id),
    onRun: () => actions.onRun(item.id),
    onExecute: () => actions.onExecute(item.id),
    onComplete: () => actions.onComplete(item.id),
    onConfigure: (values: Record<string, unknown>) => actions.onConfigure(item.id, values),
    onViewLog: () => setLogItem(item),
    lastSave: actions.lastConfigureRun(item.id),
    onDismissLastSave: () => actions.dismissConfigureRun(item.id),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) {
    return <Alert severity="error">Couldn’t load the preflight checklist.</Alert>;
  }
  if (data.length === 0) {
    return <Alert severity="info">No preflight items for this tour operator.</Alert>;
  }

  // Overall summary, independent of the search filter: status counts over the
  // applicable items, plus an N/A count of the inapplicable ones.
  // skipped / superseded are offboarding-only (this list is the onboarding checklist):
  // counted for the type's sake, never shown here.
  const counts: Record<PreflightStatus, number> = {
    pending: 0, started: 0, completed: 0, error: 0, skipped: 0, superseded: 0,
  };
  for (const i of data) {
    if (i.applicable) counts[i.status] += 1;
  }
  const naCount = data.filter((i) => !i.applicable).length;
  // Applicable items whose action automation is still a documented stub.
  const notImplementedCount = data.filter((i) => i.applicable && !i.implemented).length;
  // Setup-wizard readiness over the open applicable items: missing required
  // input vs gated by an unmet dependency.
  const needsInputCount = data.filter(
    (i) => i.applicable && i.status !== 'completed' && !i.ready,
  ).length;
  const blockedCount = data.filter(
    (i) => i.applicable && i.status !== 'completed' && i.blocked_by.length > 0,
  ).length;

  // Client-side quick filter by item name (title) or action code.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? data.filter((i) => i.title.toLowerCase().includes(q) || i.action.toLowerCase().includes(q))
    : data;
  const groups = groupItems(filtered);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ flexGrow: 1, flexWrap: 'wrap', gap: 0.75 }}>
          {STATUSES.map((s) => (
            <Chip
              key={s}
              size="small"
              color={statusChipColor(s)}
              variant={counts[s] ? 'filled' : 'outlined'}
              label={`${STATUS_LABELS[s]}: ${counts[s]}`}
            />
          ))}
          <Chip size="small" variant={naCount ? 'filled' : 'outlined'} label={`N/A: ${naCount}`} />
          {notImplementedCount > 0 && (
            <Chip size="small" color="warning" variant="outlined" label={`Not Implemented: ${notImplementedCount}`} />
          )}
          {needsInputCount > 0 && (
            <Chip size="small" color="info" variant="outlined" label={`Needs input: ${needsInputCount}`} />
          )}
          {blockedCount > 0 && (
            <Chip size="small" variant="outlined" label={`Blocked: ${blockedCount}`} />
          )}
        </Stack>
        <Link
          component={RouterLink}
          to={`/tour-operators/${to.id}/setup`}
          variant="body2"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Open setup wizard
        </Link>
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name…"
          sx={{ width: { xs: '100%', sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={expandMode}
          onChange={(_, v: ExpandMode | null) => changeMode(v)}
          aria-label="Row detail density"
        >
          <ToggleButton value="collapsed" sx={{ textTransform: 'none' }}>
            <UnfoldLessIcon fontSize="small" sx={{ mr: 0.5 }} />
            Collapsed
          </ToggleButton>
          <ToggleButton value="default" sx={{ textTransform: 'none' }}>
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
            Default
          </ToggleButton>
          <ToggleButton value="expanded" sx={{ textTransform: 'none' }}>
            <UnfoldMoreIcon fontSize="small" sx={{ mr: 0.5 }} />
            Expanded
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access. The `console_preflight_manage` permission is required to run or
          update items.
        </Alert>
      )}

      {groups.length === 0 ? (
        <Alert severity="info">No items match “{search}”.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ '& td, & th': { py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 44 }} />
              <TableCell sx={{ width: 132 }}>Status</TableCell>
              <TableCell>Item</TableCell>
              <TableCell sx={{ width: 140 }}>Owner</TableCell>
              <TableCell sx={{ width: 60 }}>Step</TableCell>
              <TableCell align="right" sx={{ width: 168 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <Fragment key={group.code}>
                <TableRow>
                  <TableCell
                    colSpan={COLS}
                    sx={{ bgcolor: 'action.hover', fontWeight: 600, color: 'text.secondary' }}
                  >
                    {group.name}
                  </TableCell>
                </TableRow>
                {applicableFirst(group.items).map((item) => (
                  <PreflightItemRow
                    key={item.id}
                    item={item}
                    topId={to.id}
                    toCode={to.code}
                    canManage={canManage}
                    pending={actions.isBusy(item.id)}
                    expanded={isExpanded(item)}
                    rowRef={(el) => {
                      rowRefs.current[item.id] = el;
                    }}
                    onJump={jumpTo}
                    onChanged={actions.invalidate}
                    onToggleExpand={() => toggleRow(item)}
                    {...handlers(item)}
                  />
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      )}

      <PreflightItemRunLogDrawer
        topId={to.id}
        itemId={logItem?.id ?? null}
        title={logItem?.title}
        open={logItem != null}
        onClose={() => setLogItem(null)}
      />
      <PreflightPreviewDialog run={actions.previewRun} onClose={actions.clearPreview} />
    </Box>
  );
}

// --------------------------------------------------------------------------

interface RowProps {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  pending: boolean;
  expanded: boolean;
  /** Registers the row element so a blocker chip elsewhere can scroll to it. */
  rowRef: (el: HTMLTableRowElement | null) => void;
  onJump: (id: number) => void;
  onChanged: () => void;
  onToggleExpand: () => void;
  onStatusChange: (status: PreflightStatus) => void;
  onStepChange: (step: number, status?: PreflightStatus) => void;
  onPreview: () => void;
  onRun: () => void;
  onExecute: () => void;
  onComplete: () => void;
  onConfigure: (values: Record<string, unknown>) => void;
  onViewLog: () => void;
  lastSave: PreflightActionRun | null;
  onDismissLastSave: () => void;
}

function PreflightItemRow({
  item,
  topId,
  toCode,
  canManage,
  pending,
  expanded,
  rowRef,
  onJump,
  onChanged,
  onToggleExpand,
  onStatusChange,
  onStepChange,
  onPreview,
  onRun,
  onExecute,
  onComplete,
  onConfigure,
  onViewLog,
  lastSave,
  onDismissLastSave,
}: RowProps) {
  const totalSteps = item.rendered_steps.length;
  // Step tracking is only meaningful for genuine multi-step tasks.
  const isMultistep = item.task_type === 'multistep' && totalSteps > 0;

  // Inapplicable: a single muted row with its reason, no controls, no detail.
  if (!item.applicable) {
    return (
      <TableRow hover sx={{ opacity: 0.55 }}>
        <TableCell />
        <TableCell>
          <Chip size="small" variant="outlined" label="N/A" />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {item.title}
          </Typography>
          {item.applicable_reason && (
            <Typography variant="caption" color="text.disabled">
              {item.applicable_reason}
            </Typography>
          )}
        </TableCell>
        <TableCell colSpan={COLS - 3} />
      </TableRow>
    );
  }

  return (
    <Fragment>
      {/* Summary line — borderless bottom so it reads as one unit with its detail. */}
      <TableRow ref={rowRef} hover sx={{ '& > td': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={onToggleExpand} aria-label={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          {canManage ? (
            <PreflightStatusControl
              status={item.status}
              disabled={pending}
              onComplete={onComplete}
              onStatusChange={onStatusChange}
            />
          ) : (
            <Chip size="small" label={STATUS_LABELS[item.status]} color={statusChipColor(item.status)} />
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {item.title}
            {item.optional && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                (opt)
              </Typography>
            )}
          </Typography>
          {!item.implemented && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Not Implemented"
              sx={{ height: 18, mt: 0.25 }}
            />
          )}
          {/* Gated on the report itself, not on `ready` — an empty report rendered
              a <Tooltip title=""> that explained nothing. */}
          {item.status !== 'completed' && item.missing_inputs.length > 0 && (
            <Tooltip title={item.missing_inputs.map((m) => `${m.label} — ${m.why}`).join('; ')}>
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label="Needs input"
                sx={{ height: 18, mt: 0.25, ml: 0.5 }}
              />
            </Tooltip>
          )}
          {item.status !== 'completed' && (
            <Box component="span" sx={{ ml: 0.5 }}>
              <BlockedChip blockers={item.blocked_by} onJump={onJump} />
            </Box>
          )}
          {!expanded && item.status === 'error' && item.error_message && (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              {item.error_message}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <AssignedGroupLink item={item} />
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {isMultistep ? `${item.step}/${totalSteps}` : '—'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <PreflightItemActionBar
            item={item}
            canManage={canManage}
            busy={pending}
            onPreview={onPreview}
            onRun={onRun}
            onExecute={onExecute}
            onComplete={onComplete}
            onViewLog={onViewLog}
          />
        </TableCell>
      </TableRow>

      {/* Detail — rendered steps + step editor + full error. */}
      <TableRow>
        <TableCell colSpan={COLS} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, pl: 6, pr: 2 }}>
              <PreflightItemDetailContent
                item={item}
                topId={topId}
                toCode={toCode}
                canManage={canManage}
                pending={pending}
                onConfigure={onConfigure}
                onStepChange={onStepChange}
                onChanged={onChanged}
                lastSave={lastSave}
                onDismissLastSave={onDismissLastSave}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
